import { Button } from '@/components/ui/Button';
import { cn } from '@/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import * as React from 'react';

const CarouselContext = React.createContext(null);

function useCarousel() {
    const context = React.useContext(CarouselContext);

    if (!context) {
        throw new Error('useCarousel must be used within a <Carousel />');
    }

    return context;
}

const Carousel = React.forwardRef((
    {
        orientation = 'horizontal',
        opts,
        setApi,
        plugins,
        className,
        children,
        ...props
    },
    ref
) => {
    const [carouselRef, api] = useEmblaCarousel({
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y'
    }, plugins);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback(api => {
        if (!api) {
            return;
        }

        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
        api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
        api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(event => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            scrollPrev();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            scrollNext();
        }
    }, [scrollPrev, scrollNext]);

    React.useEffect(() => {
        if (!api || !setApi) {
            return;
        }

        setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
        if (!api) {
            return;
        }

        onSelect(api);
        api.on('reInit', onSelect);
        api.on('select', onSelect);

        return () => {
            api?.off('select', onSelect);
        };
    }, [api, onSelect]);

    return (
        (<CarouselContext.Provider
            value={{
                carouselRef,
                api: api,
                opts,
                orientation:
                                orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
                scrollPrev,
                scrollNext,
                canScrollPrev,
                canScrollNext
            }}>
            <div
                aria-roledescription="carousel"
                className={cn('tw-relative', className)}
                onKeyDownCapture={handleKeyDown}
                ref={ref}
                role="region"
                {...props}>
                {children}
            </div>
        </CarouselContext.Provider>)
    );
});
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef(({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
        (<div className="tw-overflow-hidden" ref={carouselRef}>
            <div
                className={cn(
                    'tw-flex',
                    orientation === 'horizontal' ? 'tw--ml-4' : 'tw--mt-4 tw-flex-col',
                    className
                )}
                ref={ref}
                {...props}/>
        </div>)
    );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef(({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
        (<div
            aria-roledescription="slide"
            className={cn(
                'tw-min-w-0 tw-shrink-0 tw-grow-0 tw-basis-full',
                orientation === 'horizontal' ? 'tw-pl-4' : 'tw-pt-4',
                className
            )}
            ref={ref}
            role="group"
            {...props}/>)
    );
});
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = React.forwardRef(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
        (<Button
            className={cn(
                'tw-absolute tw- tw-h-8 tw-w-8 tw-rounded-full',
                orientation === 'horizontal'
                    ? 'tw--left-12 tw-top-1/2 tw--translate-y-1/2'
                    : 'tw--top-12 tw-left-1/2 tw--translate-x-1/2 tw-rotate-90',
                className
            )}
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            ref={ref}
            size={size}
            variant={variant}
            {...props}>
            <ArrowLeft className="tw-h-4 tw-w-4"/>
            <span className="tw-sr-only">Previous slide</span>
        </Button>)
    );
});
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
        (<Button
            className={cn('tw-absolute tw-h-8 tw-w-8 tw-rounded-full', orientation === 'horizontal'
                ? 'tw--right-12 tw-top-1/2 tw--translate-y-1/2'
                : 'tw--bottom-12 tw-left-1/2 tw--translate-x-1/2 tw-rotate-90', className)}
            disabled={!canScrollNext}
            onClick={scrollNext}
            ref={ref}
            size={size}
            variant={variant}
            {...props}>
            <ArrowRight className="tw-h-4 tw-w-4"/>
            <span className="tw-sr-only">Next slide</span>
        </Button>)
    );
});
CarouselNext.displayName = 'CarouselNext';

export { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext };
